import { defineComponent, h } from 'vue';

/**
 * @typedef {object} AiActivityGroupProps
 * @property {number} [parentConnectorHeight=24] - Height of the first connector that connects to parent (in px). Set to 0 to hide.
 * @property {number} [childConnectorHeight=12] - Height of connectors between children (in px). Set to 0 to hide.
 * @property {boolean} [showParentConnector=true] - Whether to show the parent connector line
 */

/** @template T; @typedef {import("vue").PropType<T>} PropType<T> */

export const CsAiActivityGroup = defineComponent({
  name: 'CsAiActivityGroup',
  props: {
    /** @type {PropType<AiActivityGroupProps['parentConnectorHeight']>} */
    parentConnectorHeight: {
      type: Number,
      default: 20,
    },
    /** @type {PropType<AiActivityGroupProps['childConnectorHeight']>} */
    childConnectorHeight: {
      type: Number,
      default: 16,
    },
    /** @type {PropType<AiActivityGroupProps['showParentConnector']>} */
    showParentConnector: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { slots }) {
    return () => {
      // Get default slot children
      const children = slots.default?.() || [];

      if (children.length === 0) return null;

      const elements = [];

      // Add parent connector if enabled and height > 0
      if (props.showParentConnector && props.parentConnectorHeight > 0) {
        elements.push(
          h(
            'div',
            {
              class: 'flex flex-col pl-6',
              style: { height: `${props.parentConnectorHeight}px` },
            },
            [
              h('div', {
                class:
                  'h-full w-[1px] border-r border-dashed border-brand-gray-10 dark:border-brand-gray-13',
              }),
            ]
          )
        );
      }

      // Render children with connectors between them
      children.forEach((child, index) => {
        elements.push(child);

        // Add child connector after each child except the last
        if (index < children.length - 1 && props.childConnectorHeight > 0) {
          elements.push(
            h(
              'div',
              {
                class: 'flex flex-col pl-6',
                style: { height: `${props.childConnectorHeight}px` },
              },
              [
                h('div', {
                  class:
                    'h-full w-[1px] border-r border-dashed border-brand-gray-9',
                }),
              ]
            )
          );
        }
      });

      // Create the wrapper div
      return h('div', { class: 'flex flex-col pl-0' }, elements);
    };
  },
});
